import {
  Gauge,
  Headphones,
  Phone,
  ChartLine,
  Hash,
  Wrench,
  ClipboardList,
  Code
} from 'lucide-vue-next'
import type { NavItem, ModuleMenuData, SubMenuItem } from '../types/navigation'

/**
 * Child routes that belong to a sidebar menu item but don't share its URL prefix.
 */
export const ROUTE_NAVIGATION_OVERRIDES: Array<{
  pathPrefix: string
  moduleId: string
  menuHref: string
}> = [
  {
    pathPrefix: '/uc-user/schedules',
    moduleId: 'uc-user',
    menuHref: '/uc-user/call-flow-settings'
  }
]

function normalizeNavPath(path: string): string {
  return path.replace(/\/$/, '') || '/'
}

export function findNavigationOverride(path: string) {
  const normalizedPath = normalizeNavPath(path)
  return ROUTE_NAVIGATION_OVERRIDES.find((override) => {
    const prefix = normalizeNavPath(override.pathPrefix)
    return normalizedPath === prefix || normalizedPath.startsWith(`${prefix}/`)
  })
}

/** Secondary menu paths that have a implemented route/page. */
const IMPLEMENTED_MENU_PATHS = new Set([
  '/system/accounts',
  '/system/api-keys',
  '/system/enterprises',
  '/system/integrations',
  '/system/ips-and-ports',
  '/system/sip-credentials',
  '/billing/dashboard',
  '/system/users',
  '/phone-number-purchasing',
  '/phone-numbers',
  '/uc-admin/phone-numbers',
  '/uc-admin/call-control-apps/automations',
  '/uc-admin/call-control-apps/call-parking-lots',
  '/uc-admin/call-control-apps/conference-bridges',
  '/uc-admin/call-control-apps/directories',
  '/uc-admin/call-control-apps/forwarders',
  '/uc-admin/call-control-apps/page-groups',
  '/uc-admin/call-control-apps/wakeup-calls',
  '/uc-user/automations',
  '/uc-user/blocked-numbers',
  '/uc-user/call-flow-settings',
  '/uc-user/extension-settings',
  '/uc-user/voicemails',
  '/uc-user/faxing',
  '/uc-user/messaging',
  '/uc-admin/settings-and-security/music-on-hold',
  '/uc-admin/settings-and-security/captchas',
  '/uc-admin/settings-and-security/blocked-numbers',
  '/uc-admin/settings-and-security/recordings',
  '/uc-admin/call-flow/two-way-toggles',
  '/uc-admin/call-flow/multiway-toggles',
  '/uc-admin/call-flow/api-routers',
  '/uc-admin/call-flow/tag-routers',
  '/uc-admin/call-flow/call-queues',
  '/uc-admin/call-flow/ring-groups',
  '/uc-admin/call-flow/ivr-menus',
  '/uc-admin/call-flow/schedules',
  '/uc-admin/call-flow/vip-callers',
  '/uc-admin/extensions-and-devices/extensions',
  '/uc-admin/extensions-and-devices/phonebooks',
  '/uc-admin/extensions-and-devices/provisioning-devices',
  '/uc-user/phonebooks',
  '/uc-admin/settings-and-security/webhooks',
  '/uc-admin/settings-and-security/star-codes',
  '/admin/roles-rights',
  '/admin/resellers',
  '/admin/countries',
  '/admin/customer-sip-servers',
  '/admin/carrier-sip-servers',
  '/admin/holidays',
  '/admin/provisioning',
  '/admin/job-queues',
  '/reports/administrative/automations',
  '/reports/voice/call-history',
  '/reports/voice/ivr-analysis',
  '/reports/voice/missed-calls',
  '/reports/voice/extension-analysis',
  '/reports/voice/inbound-analysis',
  '/reports/voice/call-queue-analysis',
  '/reports/email-logs',
  '/reports/message-logs'
])

function hasImplementedPage(href: string) {
  return href !== '#' && IMPLEMENTED_MENU_PATHS.has(href)
}

/** Mark leaf menu entries without a page as disabled. */
function withDisabledMenus(items: SubMenuItem[]): SubMenuItem[] {
  return items.map((item) => {
    if (item.items?.length) {
      return { ...item, items: withDisabledMenus(item.items) }
    }
    return {
      ...item,
      disabled: !hasImplementedPage(item.href)
    }
  })
}

function withDisabledModuleMenus(
  data: Record<string, ModuleMenuData>
): Record<string, ModuleMenuData> {
  return Object.fromEntries(
    Object.entries(data).map(([key, module]) => [
      key,
      { ...module, items: withDisabledMenus(module.items) }
    ])
  )
}

/**
 * Primary Module Navigation Rail items
 */
export const PRIMARY_NAV_ITEMS: NavItem[] = [
  { id: 'dashboards', label: 'Dashboards', icon: Gauge, active: true },
  { id: 'uc-user', label: 'UC User', icon: Headphones, permission: 'uc_user' },
  {
    id: 'uc-admin',
    label: 'UC Admin',
    icon: Phone,
    permission: 'uc_admin',
    roleType: ['qv', 'rs', 'ac']
  },
  { id: 'reports', label: 'Reports', icon: ChartLine, permission: 'reports' },
  { id: 'phone-numbers', label: 'Phone Numbers', icon: Hash, permission: 'phone_numbers' },
  { id: 'system', label: 'System', icon: Wrench, permission: 'system' },
  { id: 'billing', label: 'Billing', icon: ClipboardList, roleType: 'qv' },
  {
    id: 'qvara_admin',
    label: 'Qvara Admin',
    icon: Code,
    permission: 'qvara_admin',
    roleType: 'qv'
  }
]

/**
 * Secondary Menu Configuration mapping module IDs to their menu structures
 */
const SECONDARY_MENU_DATA_RAW: Record<string, ModuleMenuData> = {
  dashboards: {
    title: 'Dashboards',
    items: [
      {
        label: 'Default Dashboard',
        href: '/dashboard',
        active: true
      }
    ]
  },
  'uc-user': {
    title: 'UC User',
    permission: 'uc_user',
    items: [
      {
        label: 'Automations',
        href: '/uc-user/automations',
        permission: 'uc_user.automations.view'
      },
      {
        label: 'Blocked Numbers',
        href: '/uc-user/blocked-numbers',
        permission: 'uc_user.blocked_numbers.view'
      },
      {
        label: 'Call Flow Settings',
        href: '/uc-user/call-flow-settings',
        permission: 'uc_user.call_flow_settings.view'
      },
      { label: 'Call Queue Monitor', href: '#', permission: 'uc_user.call_queue_monitor.view' },
      {
        label: 'Digital Faxing',
        href: '/uc-user/faxing',
        permission: 'uc_user.digital_faxing.view'
      },
      {
        label: 'Extension Settings',
        href: '/uc-user/extension-settings',
        permission: 'uc_user.extension_settings.view'
      },
      { label: 'Phonebooks', href: '/uc-user/phonebooks', permission: 'uc_user.phonebooks.view' },
      {
        label: 'SMS/MMS Messaging',
        href: '/uc-user/messaging',
        permission: 'uc_user.sms/mms_messaging.view'
      },
      { label: 'Voicemails', href: '/uc-user/voicemails', permission: 'uc_user.voicemails.view' },
      { label: 'Web Dialer', href: '#', permission: 'uc_user.web_dialer.view' }
    ]
  },
  'uc-admin': {
    title: 'UC Admin',
    permission: 'uc_admin',
    roleType: ['qv', 'rs', 'ac'],
    items: [
      {
        label: 'Phone Numbers',
        href: '/uc-admin/phone-numbers',
        permission: 'uc_admin.phone_numbers.view'
      },
      {
        label: 'Call Flow',
        href: '#',
        permission: 'uc_admin.call_flow',
        items: [
          {
            label: 'API Routers',
            href: '/uc-admin/call-flow/api-routers',
            permission: 'uc_admin.call_flow.api_routers.view'
          },
          {
            label: 'Call Queues',
            href: '/uc-admin/call-flow/call-queues',
            permission: 'uc_admin.call_flow.call_queues.view'
          },
          {
            label: 'IVR Menus',
            href: '/uc-admin/call-flow/ivr-menus',
            permission: 'uc_admin.call_flow.ivr_menus.view'
          },
          {
            label: 'Ring Groups',
            href: '/uc-admin/call-flow/ring-groups',
            permission: 'uc_admin.call_flow.ring_groups.view'
          },
          {
            label: 'Schedules',
            href: '/uc-admin/call-flow/schedules',
            permission: 'uc_admin.call_flow.schedules.view'
          },
          {
            label: 'Tag Routers',
            href: '/uc-admin/call-flow/tag-routers',
            permission: 'uc_admin.call_flow.tag_routers.view'
          },
          {
            label: 'Toggles: Two-Way',
            href: '/uc-admin/call-flow/two-way-toggles',
            permission: 'uc_admin.call_flow.two_way_toggles.view'
          },
          {
            label: 'Toggles: Multiway',
            href: '/uc-admin/call-flow/multiway-toggles',
            permission: 'uc_admin.call_flow.multiway_toggles.view'
          },
          {
            label: 'VIP Callers',
            href: '/uc-admin/call-flow/vip-callers',
            permission: 'uc_admin.call_flow.vip_callers.view'
          }
        ]
      },
      {
        label: 'Call Control Apps',
        href: '#',
        permission: 'uc_admin.call_control_apps',
        items: [
          {
            label: 'Automations',
            href: '/uc-admin/call-control-apps/automations',
            permission: 'uc_admin.call_control_apps.automations.view'
          },
          {
            label: 'Call Parking Lots',
            href: '/uc-admin/call-control-apps/call-parking-lots',
            permission: 'uc_admin.call_control_apps.call_parking_lots.view'
          },
          {
            label: 'Conference Bridges',
            href: '/uc-admin/call-control-apps/conference-bridges',
            permission: 'uc_admin.call_control_apps.conference_bridges.view'
          },
          {
            label: 'Dial-by-Name Directories',
            href: '/uc-admin/call-control-apps/directories',
            permission: 'uc_admin.call_control_apps.directories.view'
          },
          {
            label: 'Forwarders',
            href: '/uc-admin/call-control-apps/forwarders',
            permission: 'uc_admin.call_control_apps.forwarders.view'
          },
          {
            label: 'Page Groups',
            href: '/uc-admin/call-control-apps/page-groups',
            permission: 'uc_admin.call_control_apps.page_groups.view'
          },
          {
            label: 'Wake-up Calls',
            href: '/uc-admin/call-control-apps/wakeup-calls',
            permission: 'uc_admin.call_control_apps.wakeup_calls.view'
          }
        ]
      },
      {
        label: 'Diagnostics',
        href: '#',
        permission: 'uc_admin.diagnostics',
        items: [
          {
            label: 'Extension Snapshot',
            href: '#',
            permission: 'uc_admin.diagnostics.extension_snapshot.view'
          },
          {
            label: 'Extension Monitor',
            href: '#',
            permission: 'uc_admin.diagnostics.extension_monitor.view'
          }
        ]
      },
      {
        label: 'Extensions & Devices',
        href: '#',
        permission: 'uc_admin.extensions_and_devices',
        items: [
          {
            label: 'Extensions',
            href: '/uc-admin/extensions-and-devices/extensions',
            permission: 'uc_admin.extensions_and_devices.extensions.view'
          },
          {
            label: 'Phonebooks',
            href: '/uc-admin/extensions-and-devices/phonebooks',
            permission: 'uc_admin.extensions_and_devices.phonebooks.view'
          },
          {
            label: 'Provisioning Devices',
            href: '/uc-admin/extensions-and-devices/provisioning-devices',
            permission: 'uc_admin.extensions_and_devices.provisioning_devices.view'
          }
        ]
      },
      {
        label: 'Settings & Security',
        href: '#',
        permission: 'uc_admin.settings_and_security',
        items: [
          {
            label: 'Blocked Numbers',
            href: '/uc-admin/settings-and-security/blocked-numbers',
            permission: 'uc_admin.settings_and_security.blocked_numbers.view'
          },
          {
            label: 'CAPTCHA',
            href: '/uc-admin/settings-and-security/captchas',
            permission: 'uc_admin.settings_and_security.captchas.view'
          },
          {
            label: 'Music on Hold',
            href: '/uc-admin/settings-and-security/music-on-hold',
            permission: 'uc_admin.settings_and_security.music_on_hold.view'
          },
          {
            label: 'Recordings',
            href: '/uc-admin/settings-and-security/recordings',
            permission: 'uc_admin.settings_and_security.recordings.view'
          },
          {
            label: 'Star Codes',
            href: '/uc-admin/settings-and-security/star-codes',
            permission: 'uc_admin.settings_and_security.star_codes.view'
          },
          {
            label: 'Webhooks',
            href: '/uc-admin/settings-and-security/webhooks',
            permission: 'uc_admin.settings_and_security.webhooks.view'
          }
        ]
      }
    ]
  },
  reports: {
    title: 'Reports',
    permission: 'reports',
    items: [
      {
        label: 'Administrative',
        href: '#',
        permission: 'reports.administrative',
        items: [
          {
            label: 'Automations',
            href: '/reports/administrative/automations',
            permission: 'reports.administrative.automations.view'
          }
        ]
      },
      { label: 'Email Logs', href: '/reports/email-logs', permission: 'reports.email_logs.view' },
      {
        label: 'Message Logs',
        href: '/reports/message-logs',
        permission: 'reports.message_logs.view'
      },
      {
        label: 'Voice',
        href: '#',
        permission: 'reports.voice',
        items: [
          {
            label: 'Call History',
            href: '/reports/voice/call-history',
            permission: 'reports.voice.call_history.view'
          },
          {
            label: 'Call Queue Analysis',
            href: '/reports/voice/call-queue-analysis',
            permission: 'reports.voice.call_queue_analysis.view'
          },
          {
            label: 'Extension Analysis',
            href: '/reports/voice/extension-analysis',
            permission: 'reports.voice.extension_analysis.view'
          },
          {
            label: 'Inbound Analysis',
            href: '/reports/voice/inbound-analysis',
            permission: 'reports.voice.inbound_analysis.view'
          },
          {
            label: 'Missed Calls',
            href: '/reports/voice/missed-calls',
            permission: 'reports.voice.missed_calls.view'
          },
          {
            label: 'IVR Analysis',
            href: '/reports/voice/ivr-analysis',
            permission: 'reports.voice.ivr_analysis.view'
          }
        ]
      }
    ]
  },
  'phone-numbers': {
    title: 'Phone Numbers',
    permission: 'phone_numbers',
    items: [
      { label: 'Manage', href: '/phone-numbers', permission: 'phone_numbers.manage.view' },
      {
        label: 'Purchase',
        href: '/phone-number-purchasing',
        permission: 'phone_numbers.purchase.view'
      }
    ]
  },
  system: {
    title: 'System',
    permission: 'system',
    items: [
      { label: 'Accounts', href: '/system/accounts', permission: 'system.accounts.view' },
      { label: 'API Keys', href: '/system/api-keys', permission: 'system.api_keys.view' },
      { label: 'Enterprises', href: '/system/enterprises', permission: 'system.enterprises.view' },
      {
        label: 'Integrations',
        href: '/system/integrations',
        permission: 'system.integrations.view'
      },
      {
        label: 'IPs and Ports',
        href: '/system/ips-and-ports',
        permission: 'system.ips_and_ports.view'
      },
      {
        label: 'SIP Credentials',
        href: '/system/sip-credentials',
        permission: 'system.accounts.view'
      },
      { label: 'Users', href: '/system/users', permission: 'system.users.view' }
    ]
  },
  billing: {
    title: 'Billing',
    roleType: 'qv',
    items: [
      {
        label: 'Dashboard',
        href: '/billing/dashboard',
        permission: 'billing.dashboard.view'
      }
    ]
  },
  qvara_admin: {
    title: 'Qvara Admin',
    permission: 'qvara_admin',
    roleType: 'qv',
    items: [
      {
        label: 'Roles & Rights',
        href: '/admin/roles-rights',
        permission: 'qvara_admin.roles.view'
      },
      { label: 'Backup & Restore', href: '#', permission: 'qvara_admin.backup_restore.view' },

      {
        label: 'Carrier SIP Servers',
        href: '/admin/carrier-sip-servers',
        permission: 'qvara_admin.carrier_sip_servers.view'
      },
      {
        label: 'Countries (ISO)',
        href: '/admin/countries',
        permission: 'qvara_admin.countries.view'
      },
      {
        label: 'Customer SIP Servers',
        href: '/admin/customer-sip-servers',
        permission: 'qvara_admin.customer_sip_servers.view'
      },
      { label: 'Docker', href: '#', permission: 'qvara_admin.docker.view' },
      {
        label: 'Job Queues',
        href: '/admin/job-queues',
        permission: 'qvara_admin.job_queues.view'
      },
      { label: 'Maintenance', href: '#', permission: 'qvara_admin.maintenance.view' },
      {
        label: 'Pre-Built Holidays',
        href: '/admin/holidays',
        permission: 'qvara_admin.holidays.view'
      },
      {
        label: 'Provisioning',
        href: '/admin/provisioning',
        permission: 'qvara_admin.provisioning.view'
      },
      {
        label: 'Resellers',
        href: '/admin/resellers',
        permission: 'qvara_admin.resellers.view'
      },
      {
        label: 'Developers',
        href: '#',
        roleType: 'qv',
        devOnly: true,
        items: [
          { label: 'CSS Classes', href: '#' },
          { label: 'Blade Components', href: '#' },
          { label: 'Livewire Components', href: '#' }
        ]
      }
    ]
  }
}

export const SECONDARY_MENU_DATA = withDisabledModuleMenus(SECONDARY_MENU_DATA_RAW)
